import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import GradientHouseIcon from "./GradientHouseIcon";
import VendorleadsGradientText from "./VendorleadsGradientText";
import ContactVendorleadsModal from "./ContactVendorleadsModal";

const FOOTER_BG = "#0f172a";
const FOOTER_TEXT = "#cbd5e1";
const FOOTER_TEXT_MUTED = "#64748b";
const FOOTER_HEADING = "#f1f5f9";
const ACCENT = "#10b981";
const LINK_HOVER = "#10b981";

const linkStyle = {
  color: FOOTER_TEXT,
  textDecoration: "none",
  fontSize: "0.875rem",
  lineHeight: "2.2",
  transition: "color 0.2s",
  display: "block",
};

const headingStyle = {
  color: FOOTER_HEADING,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "1rem",
};

const CONTENT_AI_LINKS = [
  { label: "AI Property Reports", to: "/features/monthly-ai-property-report" },
  { label: "AI Property Valuator", to: "/features/diy-ai-property-valuator" },
  { label: "AI News Articles", to: "/features/ai-news-articles" },
  { label: "AI Co-Pilot", to: "/features/ai-co-pilot" },
  { label: "Property Appraisals", to: "/features/property-appraisals" },
];

const CAMPAIGNS_TOOLS_LINKS = [
  { label: "SMS Campaigns", to: "/features/sms-campaigns" },
  { label: "QR Code Flyers", to: "/features/qr-code-flyer" },
  { label: "Map Prospector", to: "/features/map-prospector" },
];

const COMPANY_LINKS = [
  { label: "Pricing", to: "/pricing" },
  { label: "Docs", to: "/docs" },
  { label: "Blog", to: "/blog" },
  { label: "Changelog", to: "/changelog" },
  { label: "Terms & Privacy", to: "/terms" },
];

function FooterLink({ to, label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      style={{ ...linkStyle, color: hovered ? LINK_HOVER : FOOTER_TEXT }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label}
    </Link>
  );
}

export default function Footer() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <footer
      style={{
        background: `linear-gradient(180deg, ${FOOTER_BG} 0%, #020617 100%)`,
        color: FOOTER_TEXT,
        padding: 0,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Top accent line */}
      <div
        style={{
          height: "3px",
          background: "linear-gradient(90deg, #10b981, #3b82f6, #10b981)",
        }}
      />

      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: isMobile ? "2.5rem 1.5rem 1.5rem" : "3.5rem 2rem 1.5rem",
        }}
      >
        {/* Main footer grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr",
            gap: isMobile ? "2rem" : "3rem",
            marginBottom: "2.5rem",
          }}
        >
          {/* Brand column */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "1.25rem",
              }}
            >
              <GradientHouseIcon width="32" height="32" />
              <VendorleadsGradientText style={{ fontSize: "1.5rem" }} />
            </div>
            <p
              style={{
                color: FOOTER_TEXT,
                fontSize: "0.9rem",
                lineHeight: 1.7,
                marginBottom: "1.5rem",
                maxWidth: "320px",
              }}
            >
              AI-powered prospecting for Australian real estate agents.
              Automate property reports, market updates, and client
              engagement — so you can focus on closing deals.
            </p>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.8rem",
                  color: ACCENT,
                  fontWeight: 600,
                  background: "rgba(16, 185, 129, 0.1)",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                  borderRadius: "999px",
                  padding: "4px 12px",
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: ACCENT, display: "inline-block" }} />
                Australian Made
              </span>
            </div>
          </div>

          {/* Content & AI column */}
          <div>
            <p style={headingStyle}>Content & AI</p>
            {CONTENT_AI_LINKS.map((link) => (
              <FooterLink key={link.to} {...link} />
            ))}
          </div>

          {/* Campaigns & Tools column */}
          <div>
            <p style={headingStyle}>Campaigns & Tools</p>
            {CAMPAIGNS_TOOLS_LINKS.map((link) => (
              <FooterLink key={link.to} {...link} />
            ))}
          </div>

          {/* Company column */}
          <div>
            <p style={headingStyle}>Company</p>
            {COMPANY_LINKS.map((link) => (
              <FooterLink key={link.to} {...link} />
            ))}
            <span
              onClick={() => setShowContact(true)}
              style={{
                ...linkStyle,
                cursor: "pointer",
              }}
            >
              Contact Us
            </span>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.2), transparent)",
            marginBottom: "1.5rem",
          }}
        />

        {/* Bottom bar */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "center" : "center",
            gap: isMobile ? "0.75rem" : "0",
          }}
        >
          <span style={{ color: FOOTER_TEXT_MUTED, fontSize: "0.8rem" }}>
            © {new Date().getFullYear()} Vendorleads Pty Ltd. All rights reserved.
          </span>
          <span style={{ color: FOOTER_TEXT_MUTED, fontSize: "0.8rem" }}>
            Built in Sydney, Australia 🇦🇺
          </span>
        </div>
      </div>
      <ContactVendorleadsModal show={showContact} onHide={() => setShowContact(false)} />
    </footer>
  );
}
